import React, {useMemo} from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import {ICurrency} from '../../model/ICurrency';
import {IStatisticModelGroup} from '../../model/IStats';

const PIE_COLORS = ['#4f46e5', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#f97316', '#64748b', '#ec4899'];

interface StatsGroupsChartsProps {
    groups: IStatisticModelGroup[];
    preferredCurrency?: ICurrency | null;
}

function pickCurrency(groups: IStatisticModelGroup[], preferredCurrency?: ICurrency | null): ICurrency | null {
    if (preferredCurrency) {
        const match = groups.flatMap((group) => group.amount).find((balance) => balance.currency.id === preferredCurrency.id);
        if (match) {
            return match.currency;
        }
    }
    return groups[0]?.amount[0]?.currency ?? null;
}

function amountForCurrency(group: IStatisticModelGroup, currency: ICurrency | null): number {
    if (!currency) {
        return Number(group.amount[0]?.amount ?? 0);
    }
    const match = group.amount.find((balance) => balance.currency.id === currency.id);
    return Number(match?.amount ?? 0);
}

const StatsGroupsCharts: React.FunctionComponent<StatsGroupsChartsProps> = ({groups, preferredCurrency}) => {
    const currency = useMemo(() => pickCurrency(groups, preferredCurrency), [groups, preferredCurrency]);
    const showCategory = groups.some((group) => group.category_name != null);
    const showMonth = groups.some((group) => group.month != null);

    const pieData = useMemo(() => {
        if (!showCategory) {
            return [];
        }
        const totals = new Map<string, number>();
        groups.forEach((group) => {
            const name = group.category_name || 'Other';
            totals.set(name, (totals.get(name) || 0) + amountForCurrency(group, currency));
        });
        return Array.from(totals.entries()).map(([name, value]) => ({name, value}));
    }, [groups, showCategory, currency]);

    const monthData = useMemo(() => {
        if (!showMonth) {
            return [];
        }
        const totals = new Map<string, number>();
        groups.forEach((group) => {
            if (!group.month) {
                return;
            }
            totals.set(group.month, (totals.get(group.month) || 0) + amountForCurrency(group, currency));
        });
        return Array.from(totals.entries())
            .sort(([left], [right]) => left.localeCompare(right))
            .map(([month, value]) => ({month, value}));
    }, [groups, showMonth, currency]);

    if (!groups.length || (!pieData.length && !monthData.length)) {
        return null;
    }

    const useLine = monthData.length > 8;
    const symbol = currency?.symbol ?? '';

    return (
        <div className={`mt-6 grid grid-cols-1 gap-6 ${pieData.length && monthData.length ? 'md:grid-cols-2' : 'max-w-3xl'}`}>
            {pieData.length > 0 && (
                <div className="rounded-md border border-gray-200 p-4">
                    <h3 className="text-sm font-semibold text-gray-800 mb-2">By category {symbol && `(${symbol})`}</h3>
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                                    {pieData.map((entry, index) => (
                                        <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]}/>
                                    ))}
                                </Pie>
                                <Tooltip formatter={(value: number) => `${symbol} ${value}`}/>
                                <Legend/>
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}
            {monthData.length > 0 && (
                <div className="rounded-md border border-gray-200 p-4">
                    <h3 className="text-sm font-semibold text-gray-800 mb-2">By month {symbol && `(${symbol})`}</h3>
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            {useLine ? (
                                <LineChart data={monthData}>
                                    <CartesianGrid strokeDasharray="3 3"/>
                                    <XAxis dataKey="month"/>
                                    <YAxis/>
                                    <Tooltip formatter={(value: number) => `${symbol} ${value}`}/>
                                    <Line type="monotone" dataKey="value" name="Amount" stroke="#4f46e5" strokeWidth={2} dot={false}/>
                                </LineChart>
                            ) : (
                                <BarChart data={monthData}>
                                    <CartesianGrid strokeDasharray="3 3"/>
                                    <XAxis dataKey="month"/>
                                    <YAxis/>
                                    <Tooltip formatter={(value: number) => `${symbol} ${value}`}/>
                                    <Bar dataKey="value" name="Amount" fill="#4f46e5"/>
                                </BarChart>
                            )}
                        </ResponsiveContainer>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StatsGroupsCharts;
